import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  useGetAllowedPeriodOperations,
  useGetCategoriesList,
} from '@/modules/periodicTasks/apihooks';
import { useCreatePeriodicOperationUtils } from '@/modules/periodicTasks/components/Create/logic';
import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import CreateEditInboundDelivery from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery';
import {
  Loading,
  NextImage,
  Radio,
  Typography,
} from '@/shared/components/atoms';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const OperationType = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();

  const [selectedAsset] = useStore((store) => store?.asset);
  const [asset] = useStore((store) => store.asset);
  const { data, isLoading } = useGetCategoriesList();
  const { data: allowedOpeartions, refetch } = useGetAllowedPeriodOperations(
    selectedAsset?.id ?? 0,
    !!asset?.IsInventoryActive
  );

  const { getCategoryJobTypes, getFormSteps } =
    useCreatePeriodicOperationUtils();

  const { watch, control, resetField, formState, getFieldState } =
    useFormContext<IPeriodicTasks.IOperationFormSchema>();
  const operationTypeWatcher = watch('operationType');
  const applicationMethodWatcher = watch('ApplicationMethod');

  const getCardStyles = (active: boolean) => {
    return classNames(
      active ? 'bg-secondary-90 text-green-300' : 'bg-background-neutral-100'
    );
  };

  const getSubtextStyle = (active: boolean) => {
    return !active ? 'text-surface-10' : undefined;
  };

  const canChangeOperationType = getFormSteps(operationTypeWatcher?.id)
    .slice(1)
    .every((step) =>
      step.fields.every((field) => !getFieldState(field, formState).isDirty)
    );

  const handleInboundDeliveryCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  const openCreateInboundDeliveryModal = (editId?: number) =>
    openModal(
      <CreateEditInboundDelivery
        editId={editId}
        onInboundDeliveryCreated={handleInboundDeliveryCreated}
      />,
      {
        variant: 'full',
        title: t(editId ? 'edit-inbound-delivery' : 'add-new-stock'),
        titleVariant: 'h2',
        onClose: () => {
          openModal(
            <ConfirmationModal
              header={t('going-somewhere-else')}
              message={t('data-loss-warning')}
              variant='warning'
              dismissText={t('continue-editing')}
              confirmText={t('discard-and-exit')}
              onConfirmRemoveNumber={3}
              buttonsAlignment='col'
              preferredAction='dismiss'
            />
          );
        },
      }
    );

  if (isLoading) return <Loading />;

  return (
    <>
      <Typography weight='medium' className='mb-16'>
        {t('task-category')}
      </Typography>
      <Controller
        control={control}
        name='operationType'
        render={({ field: { onChange } }) => (
          <div className='grid grid-cols-2 gap-20 tablet:grid-cols-4'>
            {data?.items.map((operation) => {
              const isActive = operation.id === operationTypeWatcher?.id;
              let isDisabled = false;

              const isFertigationOperation =
                operation.name.match(/Fertigation/gi);
              const isSprayingOperation = operation.name.match(/Spraying/gi);
              const isPlantingOperation = operation.name.match(/Planting/gi);

              if (isFertigationOperation || isSprayingOperation)
                isDisabled =
                  !!asset?.IsInventoryActive &&
                  !allowedOpeartions?.FertigationAndSpraying;

              if (isPlantingOperation)
                isDisabled =
                  !!asset?.IsInventoryActive && !allowedOpeartions?.Planting;

              const popupMsgKey = isPlantingOperation
                ? 'planting-disabled-popup'
                : isFertigationOperation
                ? 'fertigation-disabled-popup'
                : isSprayingOperation
                ? 'spraying-disabled-popup'
                : '';

              const content = isDisabled ? (
                <TippyTooltip
                  content={
                    <div className='h-[96px] w-[260px]'>
                      <Trans
                        i18nKey={popupMsgKey}
                        components={{
                          a: (
                            <span
                              className='inline cursor-pointer font-bold text-primary-default underline'
                              onClick={() => openCreateInboundDeliveryModal()}
                            />
                          ),
                        }}
                      />
                    </div>
                  }
                >
                  <>
                    <NextImage
                      src={operation.icon}
                      width={30}
                      height={30}
                      alt={operation.name}
                    />
                    <Typography weight='medium'>{t(operation.name)}</Typography>
                    {/* TODO: description */}
                    <Typography
                      variant='caption'
                      className={getSubtextStyle(isActive)}
                    >
                      {t(operation.description)}
                    </Typography>
                  </>
                </TippyTooltip>
              ) : (
                <>
                  <NextImage
                    src={operation.icon}
                    width={30}
                    height={30}
                    alt={operation.name}
                  />
                  <Typography weight='medium'>{t(operation.name)}</Typography>
                  {/* TODO: description */}
                  <Typography
                    variant='caption'
                    className={getSubtextStyle(isActive)}
                  >
                    {t(operation.description)}
                  </Typography>
                </>
              );

              return (
                <div
                  key={operation.id}
                  className={classNames(
                    'flex cursor-pointer flex-col gap-8 rounded-xl px-24 py-16',
                    !canChangeOperationType && 'pointer-events-none opacity-5',
                    `${isDisabled ? 'opacity-5' : ''}`,
                    getCardStyles(isActive)
                  )}
                  onClick={() => {
                    if (!isDisabled) {
                      resetField('ApplicationMethod');
                      onChange({
                        name: operation.name,
                        id: operation.id,
                        icon: operation.icon,
                      });
                    }
                  }}
                >
                  {content}
                </div>
              );
            })}
          </div>
        )}
      />
      {operationTypeWatcher && data && (
        <div className='mt-40'>
          <Typography weight='medium' className='mb-16'>
            {t('application-method')}
          </Typography>
          <Controller
            control={control}
            name='ApplicationMethod'
            render={({ field: { onChange } }) => (
              <div className='grid grid-cols-1 gap-x-20 gap-y-16 tablet:grid-cols-2'>
                {getCategoryJobTypes(operationTypeWatcher.id, data.items)?.map(
                  (jobType) => {
                    const isActive =
                      applicationMethodWatcher?.id === jobType.id;
                    return (
                      <div
                        className={classNames(
                          'cursor-pointer rounded-xl p-24',
                          getCardStyles(isActive)
                        )}
                        key={jobType.id}
                        onClick={() => {
                          onChange(jobType);
                        }}
                      >
                        <Typography
                          weight='medium'
                          className='mb-12 flex items-center gap-12'
                        >
                          <Radio
                            readOnly
                            variant='success'
                            checked={isActive}
                          />
                          {jobType.name}
                        </Typography>
                        <Typography
                          variant='caption'
                          className={getSubtextStyle(isActive)}
                        >
                          {t(jobType.description)}
                        </Typography>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          />
        </div>
      )}
    </>
  );
};

export default OperationType;
